// src/components/checkout/OrderSummary.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import { useProducts } from '../../context/ProductContext';
import { useTheme } from '../ThemeProvider';
import Button from '../common/Button';
import Receipt from './Receipt';
import { fetchData } from '../../utils/api';

function OrderSummary({ cart, total: subtotal, paymentConfirmed = false, shippingAddress, readOnly = false }) {
  const { formatPrice } = useCurrency();
  const { products } = useProducts();
  const { primaryColor } = useTheme();
  const [business, setBusiness] = useState({ name: '', abn: '' });
  const [vatRate, setVatRate] = useState(0);
  const [shippingFee, setShippingFee] = useState(null);
  const [error, setError] = useState('');
  const [validCart, setValidCart] = useState([]);

  useEffect(() => {
    const fetchMockApi = async () => {
      try {
        const data = await fetchData('/website/business-details', { noCache: true });
        setBusiness(data.business || { name: 'EcoMart', abn: 'Unknown' });
        setVatRate(data.fees?.vat?.vatRate || 0);
        setShippingFee(data.fees?.shipping?.shippingFee || 0);
      } catch (err) {
        setError('Failed to load business details or fees. Using defaults.');
        setBusiness({ name: 'EcoMart', abn: 'Unknown' });
        setVatRate(0);
        setShippingFee(0);
      }
    };
    fetchMockApi();
  }, []);

  useEffect(() => {
    const validated = cart
      .map((item) => {
        const product = products.find((p) => p.id === item.id);
        if (!product || ['real_estate', 'cars'].includes(product.category)) {
          return null;
        }
        return { ...item, product };
      })
      .filter(Boolean);
    setValidCart(validated);
  }, [cart, products]);

  const vat = subtotal * (vatRate / 100);
  const shipping = shippingFee || 0;
  const grandTotal = subtotal + vat + shipping;

  const getImageSrc = (item) => {
    const product = item.product;
    if (product?.category === 'books' && product.imageViews) {
      return process.env.REACT_APP_PRODUCT_URL + product.image || '/assets/images/placeholder.jpg';
    }
    if ((product?.category === 'clothing' || product?.category === 'shoes') && item.selectedColor) {
      return process.env.REACT_APP_PRODUCT_URL + product.image || '/assets/images/placeholder.jpg';
    }
    return process.env.REACT_APP_PRODUCT_URL + (product?.image || 'placeholder.jpg');
  };

  return (
    <div className="bg-cream p-6 rounded-lg shadow-md border border-primary/10">
      <h2 className="text-xl font-playfair-display font-semibold text-primary mb-2">Order Summary</h2>
      <p className="text-sm font-montserrat text-primary/70 mb-4">
        {business.name} (ABN: {business.abn})
      </p>
      {error && <p className="text-sm font-montserrat text-red-600 mb-4">{error}</p>}

      {validCart.length > 0 ? (
        <div className="space-y-4" role="list" aria-label="Cart items in order summary">
          {validCart.map((item) => (
            <div
              key={item.variantKey || `${item.id}-${item.selectedColor || ''}-${item.selectedSize || ''}`}
              className="flex items-center gap-4 p-3 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow"
              role="listitem"
            >
              <img
                src={getImageSrc(item)}
                alt={`${item.name} in order summary`}
                className="w-16 h-16 object-cover rounded"
                onError={(e) => { e.target.src = '/assets/images/placeholder.jpg'; }}
              />
              <div className="flex-1">
                <p className="font-montserrat text-primary font-medium">{item.name}</p>
                <p className="font-montserrat text-sm text-primary/70">
                  Qty: {item.quantity}
                  {item.selectedSize && ` • Size: ${item.selectedSize}`}
                  {item.selectedColor && ` • Color: ${item.selectedColor}`}
                  {item.format && ` • Format: ${item.format}`}
                </p>
              </div>
              <p className="font-montserrat text-primary font-medium">
                {formatPrice ? formatPrice(item.quantity * item.price) : `₦${(item.quantity * item.price).toFixed(2)}`}
              </p>
            </div>
          ))}

          <div className="border-t border-primary/20 pt-4 mt-4">
            <div className="flex justify-between font-montserrat text-secondary mb-2">
              <span>Subtotal</span>
              <span>{formatPrice ? formatPrice(subtotal) : `₦${subtotal.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between font-montserrat text-secondary mb-2">
              <span>VAT ({vatRate}%)</span>
              <span>{formatPrice ? formatPrice(vat) : `₦${vat.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between font-montserrat text-secondary mb-4">
              <span>Shipping</span>
              <span>
                {shippingFee === null
                  ? 'Calculated at next step'
                  : formatPrice
                  ? formatPrice(shipping)
                  : `₦${shipping.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between font-montserrat text-lg font-semibold text-primary">
              <span>Total</span>
              <span>{formatPrice ? formatPrice(grandTotal) : `₦${grandTotal.toFixed(2)}`}</span>
            </div>
          </div>

          {/* {paymentConfirmed && shippingAddress && (
            <Receipt
              cart={validCart}
              subtotal={subtotal}
              vat={vat}
              shipping={shipping}
              total={grandTotal}
              formatPrice={formatPrice}
              business={business}
              shippingAddress={shippingAddress}
              customer={{
                full_name: shippingAddress.name || 'Guest Customer',
                email: shippingAddress.email || '',
                phone: shippingAddress.phone || '',
              }}
            />
          )} */}

          {paymentConfirmed && shippingAddress && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                Receipt will be available after successful payment
              </p>
              <Button
                disabled
                className="bg-gray-300 text-gray-600 cursor-not-allowed px-6 py-3"
              >
                Download Receipt (After Payment)
              </Button>
            </div>
          )}

          {!readOnly && (
            <Link
              to="/cart"
              className="block w-full text-center font-montserrat text-primary py-2 hover:underline"
              aria-label="Edit cart from order summary"
            >
              Edit Cart
            </Link>
          )}
        </div>
      ) : (
        <p className="font-montserrat text-primary" role="alert">
          No valid items in your order.
        </p>
      )}
    </div>
  );
}

export default OrderSummary;